import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Globe, Users, FileText, Shield } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function BusinessImmigrationLaw() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Globe, title: 'Global Talent Access', description: 'Bring skilled workers and executives to your U.S. operations.' },
    { icon: Users, title: 'Visa Strategy', description: 'Comprehensive visa planning for employees and investors.' },
    { icon: FileText, title: 'Complete Support', description: 'Handle all immigration paperwork and compliance requirements.' },
    { icon: Shield, title: 'Risk Management', description: 'Ensure compliance with complex immigration regulations.' },
  ];

  const faqs = [
    { question: 'What is business immigration law?', answer: 'Business immigration law governs the process of bringing foreign nationals to the U.S. for employment, investment, or business purposes through various visa categories.' },
    { question: 'What types of work visas are available?', answer: 'Common work visas include H-1B (specialty occupation), L-1 (intracompany transfer), E-2 (investor), O-1 (extraordinary ability), and TN (NAFTA professionals).' },
    { question: 'How long does the visa process take?', answer: 'Processing times vary by visa type, ranging from a few months to over a year. Premium processing is available for some visa categories for expedited review.' },
    { question: 'What are the costs involved?', answer: 'Costs include government filing fees, attorney fees, and potential premium processing fees. Total costs vary significantly by visa type and case complexity.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Business Immigration Law</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Navigate complex immigration requirements to bring global talent to your business.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our Immigration Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Expert guidance for business immigration and work visas.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about business immigration law.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Ready to Navigate Business Immigration?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Get expert legal support for your business immigration needs.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="business_immigration" />
      <Footer />
    </div>
  );
}